import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import DynamicTitle from "./DynamicTitle";
import WaveDivider from "./WaveDivider";
import { trackEvent } from "../utils/posthog";
import { trackInitiateCheckout } from "../utils/metaPixel";

/**
 * Landing Page: Build Balance in Your Life
 * Tied to winning A/B ad about building balance.
 * Themes: balance, meditation comparison, focus, peace.
 * Conversion-optimized: minimal eject options, CTAs only (Demo or Free Trial).
 */

const STRIPE_LINKS = {
  monthly: "https://buy.stripe.com/5kQbJ18MweJIbT90GteME04",
  annual: "https://buy.stripe.com/fZu6oH2o89poe1hbl7eME06",
};

function BalanceLandingPage() {
  const location = useLocation();
  useEffect(() => {
    trackEvent("landing_page_viewed", {
      variant: "balance",
      variant_name: "build_balance_in_life",
      page_url: window.location.href,
    });
  }, []);

  const handleCheckoutClick = (plan) => {
    trackEvent("checkout_initiated", {
      variant: "balance",
      plan_type: plan,
      source: "landing_page",
    });
    trackInitiateCheckout({ content_type: "product", content_ids: [plan] });
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Build Balance in Your Life — RealignMind</DynamicTitle>

      {/* Minimal header: logo + single CTA only */}
      <header className="bg-background py-4 px-4 border-b border-tint">
        <div className="max-w-6xl mx-auto flex justify-between items-center gap-3">
          <span className="font-bold text-xl sm:text-2xl text-primary truncate min-w-0">RealignMind</span>
          <div className="flex gap-2 sm:gap-3 flex-shrink-0">
            <Link
              to="/demo"
              state={{ from: location.pathname }}
              className="text-sm font-medium text-accent hover:text-primary min-h-[44px] inline-flex items-center justify-center px-3 py-2 rounded-full transition-colors touch-manipulation"
            >
              Try Demo
            </Link>
            <a
              href={STRIPE_LINKS.annual}
              onClick={() => handleCheckoutClick("annual")}
              className="text-sm font-bold bg-accent hover:bg-accentDark text-white px-4 py-2 min-h-[44px] inline-flex items-center justify-center rounded-full transition-colors touch-manipulation"
            >
              Start Free Trial
            </a>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        {/* Hero: Build Balance */}
        <section className="bg-surfaceTint py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text mb-6 leading-tight">
              Build Balance in Your Life.
              <br />
              <span className="text-accent">Focus. Peace. Clarity.</span>
            </h1>
            <p className="text-lg md:text-xl text-textSecondary mb-4 max-w-2xl mx-auto">
              You've tried deep breaths. Maybe even meditation. But when your mind is
              racing, willpower alone doesn't cut it. Bilateral stimulation is different —
              gentle left-right eye movement that helps quiet the noise and restore
              focus. In minutes, not months.
            </p>
            <p className="text-base text-textSecondary mb-8 max-w-xl mx-auto">
              RealignMind brings this research-backed technique to your screen.
              Follow the dot. Find your center.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={STRIPE_LINKS.annual}
                onClick={() => handleCheckoutClick("annual")}
                className="w-full sm:w-auto inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Start Free Trial
              </a>
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="w-full sm:w-auto inline-flex items-center justify-center bg-white hover:bg-surfaceTint text-text font-bold py-4 px-8 rounded-full text-lg border border-tint transition-colors"
              >
                Try the Free Demo
              </Link>
            </div>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-10 text-sm text-textSecondary">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span><strong>30-day trial with annual</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span><strong>Unlimited sessions</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span><strong>Cancel anytime</strong></span>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="transparent" bottomColor="background" />

        {/* Product GIF + Meditation comparison */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <div className="md:flex md:gap-12 items-center">
              <div className="md:w-1/2 mb-8 md:mb-0">
                <div className="rounded-2xl overflow-hidden shadow-sm border border-tint">
                  <video
                    src="/videos/SlowDotFull.mp4"
                    className="w-full h-auto"
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-label="Bilateral stimulation in action — follow the dot to build focus and peace"
                  />
                </div>
                <p className="text-center text-sm text-textSecondary mt-3">
                  Simply follow the dot with your eyes
                </p>
              </div>
              <div className="md:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">
                  Not Meditation. Something Different.
                </h2>
                <p className="text-textSecondary mb-4">
                  Meditation trains you to observe thoughts. Bilateral stimulation works
                  with your nervous system directly—rhythmic left-right input helps calm
                  the brain and support emotional processing.
                </p>
                <p className="text-textSecondary mb-4">
                  Many people find it easier to build focus and peace with bilateral
                  stimulation than with sitting meditation alone.
                </p>
                <ul className="space-y-2">
                  {["Build focus and clarity", "Find peace in minutes", "Use anytime, anywhere"].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-textSecondary">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link
                    to="/demo"
                    state={{ from: location.pathname }}
                    className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-3 px-6 rounded-full transition-colors"
                  >
                    Try the Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* How It Works */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-3">
              How It Works
            </h2>
            <p className="text-center text-textSecondary mb-10 max-w-xl mx-auto">
              No experience needed. No learning curve. Just open it and go.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-10">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  1
                </div>
                <h3 className="font-semibold text-text mb-2">Pick a session</h3>
                <p className="text-sm text-textSecondary">
                  Choose guided or open-ended. Sessions range from 5 to 20 minutes — fit them into any part of your day.
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  2
                </div>
                <h3 className="font-semibold text-text mb-2">Follow the dot</h3>
                <p className="text-sm text-textSecondary">
                  Watch the dot move left to right. That's it. The bilateral movement does the work while you let your mind unwind.
                </p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  3
                </div>
                <h3 className="font-semibold text-text mb-2">Feel the shift</h3>
                <p className="text-sm text-textSecondary">
                  Most people notice a calmer, clearer feeling within a single session. Use it before a stressful meeting, after a hard day, or whenever you need to reset.
                </p>
              </div>
            </div>
            <div className="text-center">
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="inline-flex items-center justify-center bg-white hover:bg-surfaceTint text-text font-bold py-3 px-6 rounded-full border border-tint transition-colors"
              >
                See it in action — Try the Free Demo
              </Link>
            </div>
          </div>
        </section>

        {/* Focus + Peace benefits */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-6">
              Build Focus. Find Peace.
            </h2>
            <p className="text-textSecondary mb-10 max-w-2xl mx-auto text-center">
              When life feels off-balance, bilateral stimulation can help you reset.
              Research-backed. Simple. Yours to use whenever you need it.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Sharpen Focus</h3>
                <p className="text-sm text-textSecondary">
                  Clear mental fog. Support clearer thinking when you need it most.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Find Peace</h3>
                <p className="text-sm text-textSecondary">
                  Calm your nervous system. Create space for balance in your day.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Restore Balance</h3>
                <p className="text-sm text-textSecondary">
                  When things feel off, a few minutes can help you realign.
                </p>
              </div>
            </div>

            <div className="text-center">
              <a
                href={STRIPE_LINKS.annual}
                onClick={() => handleCheckoutClick("annual")}
                className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Start Free Trial
              </a>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Pricing */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-2">
              Simple Pricing
            </h2>
            <p className="text-center text-textSecondary mb-10">
              Start free. Cancel anytime. No hidden fees.
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-gray-300 transition-colors">
                <h3 className="text-xl font-bold text-text mb-2">Monthly</h3>
                <p className="text-textSecondary text-sm mb-4">Flexible month-to-month</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-text">$19</span>
                  <span className="text-textSecondary">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm text-textSecondary">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    7-day free trial
                  </li>
                  <li className="flex items-center gap-2 text-sm text-textSecondary">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Unlimited sessions
                  </li>
                  <li className="flex items-center gap-2 text-sm text-textSecondary">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Cancel anytime
                  </li>
                </ul>
                <a
                  href={STRIPE_LINKS.monthly}
                  onClick={() => handleCheckoutClick("monthly")}
                  className="block w-full text-center bg-background hover:bg-surfaceTint text-text font-semibold py-3 rounded-full border border-tint transition-colors"
                >
                  Start 7-Day Trial
                </a>
              </div>

              <div className="bg-gradient-to-br from-accent to-primary rounded-2xl p-8 text-white relative">
                <div className="absolute -top-3 right-4 bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full">
                  SAVE $48
                </div>
                <h3 className="text-xl font-bold mb-2">Annual</h3>
                <p className="text-white/80 text-sm mb-4">Best value for committed wellness</p>
                <div className="mb-2">
                  <span className="text-4xl font-bold">$15</span>
                  <span className="text-white/80">/month</span>
                </div>
                <p className="text-sm text-white/80 mb-6">
                  <span className="line-through opacity-75">$228</span> $180/year
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    <strong>30-day free trial</strong>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Unlimited sessions
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Save $48/year
                  </li>
                </ul>
                <a
                  href={STRIPE_LINKS.annual}
                  onClick={() => handleCheckoutClick("annual")}
                  className="block w-full text-center bg-white text-accent hover:bg-surfaceTint font-bold py-3 rounded-full transition-colors"
                >
                  Start Free Trial
                </a>
              </div>
            </div>

            <div className="text-center mt-8 px-2">
              <div className="inline-flex flex-wrap items-center justify-center gap-2 bg-secondary/10 text-secondary px-4 sm:px-6 py-3 rounded-full text-left sm:text-center max-w-full">
                <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base"><strong>30-Day Money-Back Guarantee</strong> — Not satisfied? Full refund, no questions.</span>
              </div>
              <p className="text-center text-sm text-textSecondary mt-4 px-2">
                Money tight? If cost is a consideration at all, email{" "}
                <a href="mailto:access@realignmind.com?subject=Financial%20Assistance%20Request" className="text-accent hover:underline font-medium">
                  access@realignmind.com
                </a>{" "}
                and we'll get you access.
              </p>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* FAQ */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-10">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              <details className="bg-white rounded-xl p-4 sm:p-6 group border border-tint">
                <summary className="flex justify-between items-start sm:items-center gap-3 cursor-pointer list-none font-semibold text-text text-left min-h-[44px] py-1 pr-1 touch-manipulation">
                  <span className="flex-1 min-w-0">Is RealignMind a replacement for therapy?</span>
                  <svg className="w-5 h-5 text-textSecondary flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  No. RealignMind is a self-directed wellness tool designed to complement your overall
                  well-being routine. It does not diagnose, treat, cure, or prevent any disease or medical
                  condition. If you're experiencing mental health challenges, please consult a healthcare professional.
                </p>
              </details>

              <details className="bg-white rounded-xl p-4 sm:p-6 group border border-tint">
                <summary className="flex justify-between items-start sm:items-center gap-3 cursor-pointer list-none font-semibold text-text text-left min-h-[44px] py-1 pr-1 touch-manipulation">
                  <span className="flex-1 min-w-0">How does the free trial work?</span>
                  <svg className="w-5 h-5 text-textSecondary flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  You get full access to all features during your trial (7 days for monthly, 30 days for annual).
                  No charge until your trial ends. Cancel anytime before then and you won't be charged at all.
                </p>
              </details>

              <details className="bg-white rounded-xl p-4 sm:p-6 group border border-tint">
                <summary className="flex justify-between items-start sm:items-center gap-3 cursor-pointer list-none font-semibold text-text text-left min-h-[44px] py-1 pr-1 touch-manipulation">
                  <span className="flex-1 min-w-0">Is my data private and secure?</span>
                  <svg className="w-5 h-5 text-textSecondary flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  Absolutely. We never sell your data to third parties. Your session information is private
                  and protected. Read our full{" "}
                  <Link to="/privacy-policy" className="text-accent hover:underline">privacy policy</Link> for details.
                </p>
              </details>

              <details className="bg-white rounded-xl p-4 sm:p-6 group border border-tint">
                <summary className="flex justify-between items-start sm:items-center gap-3 cursor-pointer list-none font-semibold text-text text-left min-h-[44px] py-1 pr-1 touch-manipulation">
                  <span className="flex-1 min-w-0">What if it doesn't work for me?</span>
                  <svg className="w-5 h-5 text-textSecondary flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  We offer a 30-day money-back guarantee even after your trial ends. If you're not satisfied,
                  contact us for a full refund—no questions asked.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-surfaceTint rounded-3xl p-8 md:p-12 border border-tint text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-text mb-2">
                Ready to Build Balance?
              </h2>
              <p className="text-textSecondary mb-6">
                Try the demo free. Or start your 30-day trial. No credit card required for the demo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/demo"
                  state={{ from: location.pathname }}
                  className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
                >
                  Try the Free Demo
                </Link>
                <a
                  href={STRIPE_LINKS.annual}
                  onClick={() => handleCheckoutClick("annual")}
                  className="inline-flex items-center justify-center bg-white hover:bg-surfaceTint text-text font-bold py-4 px-8 rounded-full text-lg border border-tint transition-colors"
                >
                  Start Free Trial
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Minimal footer: legal only, no nav links */}
        <footer className="bg-secondary text-white py-8 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-textSecondary mb-3">
              Built-in safety features with immediate access to crisis support resources.
            </p>
            <p className="text-xs text-textSecondary mb-2">
              RealignMind is a wellness tool and does not diagnose, treat, cure, or
              prevent any disease or medical condition. Not a substitute for professional care.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/privacy-policy" className="text-white/80 hover:text-white">
                Privacy
              </Link>
              <Link to="/terms-of-service" className="text-white/80 hover:text-white">
                Terms
              </Link>
              <Link to="/emergency-support" className="text-white/80 hover:text-white">
                Crisis Help
              </Link>
            </div>
          </div>
        </footer>
      </main>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden z-50 shadow-sm">
        <div className="flex gap-3">
          <a
            href={STRIPE_LINKS.annual}
            onClick={() => handleCheckoutClick("annual")}
            className="flex-1 text-center bg-accent hover:bg-accentDark text-white font-bold py-3 px-4 rounded-full transition-colors"
          >
            Free Trial
          </a>
          <Link
            to="/demo"
            state={{ from: location.pathname }}
            className="flex-1 text-center bg-background hover:bg-surfaceTint text-text font-bold py-3 px-4 rounded-full border border-tint transition-colors"
          >
            Try Demo
          </Link>
        </div>
      </div>

      <div className="h-20 md:hidden" />
    </div>
  );
}

export default BalanceLandingPage;
